import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Layout from '../components/Layout';
import { blogService } from '../services/firebaseService';
import { BlogPost } from '../types';
import { Timestamp } from 'firebase/firestore';

const BlogPostDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPost = async () => {
      if (!id) {
        setError('Post ID is required');
        setLoading(false);
        return;
      }

      try {
        const postData = await blogService.getBlogPost(id);
        if (postData) {
          setPost(postData);
        } else {
          setError('Post not found');
        }
      } catch (err) {
        console.error('Error loading blog post:', err);
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return '';
    let date: Date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp as any);
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="loading"><div className="spinner" /></div>
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="container">
          <div className="error">{error || 'Post not found'}</div>
          <Link to="/blog" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Back to Blog
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <Link 
          to="/blog" 
          className="btn btn-secondary"
          style={{ marginBottom: '2rem', display: 'inline-block' }}
        >
          ← Back to Blog
        </Link>
        
        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ whiteSpace: 'pre-line' }}>{post.title}</h1>
          {post.author && (
            <p style={{ color: 'var(--text-light)', fontSize: '1rem', marginBottom: '0.5rem' }}>
              Written by: {post.author}
            </p>
          )}
          {post.publishedAt && (
            <p style={{ color: 'var(--text-light)', fontSize: '1rem', marginBottom: '2rem' }}>
              Published: {formatDate(post.publishedAt)}
            </p>
          )}
          <div 
            style={{ 
              marginTop: '2rem',
              lineHeight: '1.8',
              color: 'var(--text-dark)'
            }}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => <p style={{ marginBottom: '1rem' }}>{children}</p>,
                h1: ({ children }) => <h1 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{children}</h1>,
                h2: ({ children }) => <h2 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}>{children}</h2>,
                h3: ({ children }) => <h3 style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}>{children}</h3>,
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                    {children}
                  </a>
                ),
                ul: ({ children }) => <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>{children}</ul>,
                ol: ({ children }) => <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>{children}</ol>,
                li: ({ children }) => <li style={{ marginBottom: '0.5rem' }}>{children}</li>
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BlogPostDetailPage;

